import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppUser } from '~/components/molecules/appuser/appuser-context';
import { AIAssistantContext } from '~/components/organisms/ai-assistant-widget/types';

export interface UseAIAssistantContextOptions {
  /**
   * Application identifier
   */
  application: 'pf-client' | 'as-client';
  /**
   * Current entity context (optional)
   */
  entity?: {
    type: string;
    id?: string;
    name?: string;
    status?: string;
  };
  /**
   * Page-specific data (optional)
   */
  pageData?: {
    selectedIds?: string[];
    filters?: Record<string, unknown>;
    activeTab?: string;
    formData?: Record<string, unknown>;
  };
  /**
   * Additional metadata to include in context
   */
  metadata?: Record<string, unknown>;
  /**
   * Whether to automatically update context on route changes
   */
  autoUpdate?: boolean;
}

/**
 * Hook to manage AI Assistant context
 * Automatically captures user, project, and route information
 *
 * @example
 * // Basic usage in app.tsx - auto-tracks route/user changes
 * useAIAssistantContext({ application: 'as-client', autoUpdate: true });
 *
 * @example
 * // In a detail view - set entity context
 * const { setEntity, setPageData } = useAIAssistantContext({
 *   application: 'as-client',
 *   autoUpdate: true,
 * });
 *
 * useEffect(() => {
 *   setEntity({
 *     type: 'work-package',
 *     id: workPackage.id,
 *     name: workPackage.name,
 *     status: workPackage.status,
 *   });
 * }, [workPackage]);
 *
 * @example
 * // In a list view - track selections and filters
 * const { setPageData } = useAIAssistantContext({
 *   application: 'as-client',
 *   autoUpdate: true,
 * });
 *
 * useEffect(() => {
 *   setPageData({
 *     selectedIds: selectedItems.map(item => item.id),
 *     filters: currentFilters,
 *     activeTab: activeTabName,
 *   });
 * }, [selectedItems, currentFilters, activeTabName]);
 */
export const useAIAssistantContext = (options: UseAIAssistantContextOptions) => {
  const { application, entity, pageData, metadata = {}, autoUpdate = true } = options;
  const location = useLocation();
  const { user } = useAppUser();
  const previousContextRef = useRef<string>('');
  const currentContextRef = useRef<AIAssistantContext | null>(null);

  // Build context from current app state
  const buildContext = useCallback((): AIAssistantContext => {
    // Extract module name from path
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const module = pathSegments[0] || 'dashboard';

    const context: AIAssistantContext = {
      route: location.pathname,
      module,
      application,
      user: {
        id: user?.id || undefined,
        firstName: user?.firstName || undefined,
        lastName: user?.lastName || undefined,
        email: user?.email || undefined,
        bun: user?.bun || undefined,
        roles: user?.roles?.map((role) => role.name) || undefined,
      },
      project: {
        id: user?.project?.id || undefined,
        name: user?.project?.name || undefined,
        number: user?.project?.jobNumber || undefined,
      },
      entity: currentContextRef.current?.entity || entity,
      pageData: currentContextRef.current?.pageData || pageData,
      metadata: { ...metadata, ...currentContextRef.current?.metadata },
      timestamp: Date.now(),
    };

    currentContextRef.current = context;
    return context;
  }, [location.pathname, application, user, entity, pageData, metadata]);

  // Send context to AI Assistant
  const sendContext = useCallback((context: AIAssistantContext) => {
    const contextString = JSON.stringify(context);

    // Only update if context has changed
    if (contextString === previousContextRef.current) {
      return;
    }

    previousContextRef.current = contextString;

    // Call global update function if it exists
    if (typeof (window as any).__aiAssistantUpdateContext === 'function') {
      (window as any).__aiAssistantUpdateContext(context);
    }

    // Also try the provider-based context send
    if (typeof (window as any).__aiAssistantSendContext === 'function' && (window as any).__aiAssistantContext) {
      (window as any).__aiAssistantSendContext(context);
    }
  }, []);

  // Update entity context - call this when viewing specific items
  const setEntity = useCallback(
    (newEntity: AIAssistantContext['entity']) => {
      if (currentContextRef.current) {
        currentContextRef.current.entity = newEntity;
      }
      const context = buildContext();
      context.entity = newEntity;
      sendContext(context);
    },
    [buildContext, sendContext]
  );

  // Update page data - call this when selections/filters change
  const setPageData = useCallback(
    (newPageData: AIAssistantContext['pageData']) => {
      if (currentContextRef.current) {
        currentContextRef.current.pageData = newPageData;
      }
      const context = buildContext();
      context.pageData = newPageData;
      sendContext(context);
    },
    [buildContext, sendContext]
  );

  // Update metadata - call this for any additional info
  const setMetadata = useCallback(
    (newMetadata: Record<string, unknown>) => {
      if (currentContextRef.current) {
        currentContextRef.current.metadata = { ...currentContextRef.current.metadata, ...newMetadata };
      }
      const context = buildContext();
      context.metadata = { ...context.metadata, ...newMetadata };
      sendContext(context);
    },
    [buildContext, sendContext]
  );

  // Clear entity context (e.g., when navigating away from detail view)
  const clearEntity = useCallback(() => {
    if (currentContextRef.current) {
      currentContextRef.current.entity = undefined;
    }
    const context = buildContext();
    context.entity = undefined;
    sendContext(context);
  }, [buildContext, sendContext]);

  // Auto-update context when route or user changes
  useEffect(() => {
    if (autoUpdate) {
      const context = buildContext();
      sendContext(context);
    }
  }, [location.pathname, user?.id, user?.project?.id, autoUpdate, buildContext, sendContext]);

  return {
    /**
     * Manually update full AI Assistant context
     */
    updateContext: (customContext?: Partial<AIAssistantContext>) => {
      const context = customContext ? { ...buildContext(), ...customContext } : buildContext();
      sendContext(context);
    },
    /**
     * Set the entity being viewed (work package, weld, etc.)
     */
    setEntity,
    /**
     * Set page-specific data (selections, filters, active tab)
     */
    setPageData,
    /**
     * Set additional metadata
     */
    setMetadata,
    /**
     * Clear the current entity context
     */
    clearEntity,
    /**
     * Build context object without updating
     */
    buildContext,
    /**
     * Get current context
     */
    getCurrentContext: () => currentContextRef.current,
  };
};
